import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { adminApi } from "../../lib/adminApi";
import { toast } from "sonner";
import { ExternalLink, ArrowLeft, Trash2, Copy, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

export default function StoreDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    adminApi.getStore(id)
      .then(setStore)
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm("Delete this store?")) return;
    try {
      await adminApi.deleteStore(id);
      toast.success("Store deleted");
      navigate("/admin/stores");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const copyStoreUrl = () => {
    const url = `${window.location.origin}/store/${id}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("URL copied!");
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("/api")) return `${API}${url}`;
    return url;
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;
  if (!store) return <div className="text-center py-8">Store not found</div>;

  const collections = store.collections || [];
  const allProducts = collections.flatMap(col =>
    (col.products || []).map(p => ({ ...p, collection: col.name }))
  );

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin/stores")}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{store.brand?.name || "Store"}</h1>
            <p className="text-gray-500 text-sm">{store.source_url}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={copyStoreUrl}>
            {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
            Copy URL
          </Button>
          <a href={`/store/${id}`} target="_blank" rel="noopener noreferrer">
            <Button variant="outline">
              <ExternalLink className="w-4 h-4 mr-1" /> View Store
            </Button>
          </a>
          <Button variant="destructive" onClick={handleDelete}>
            <Trash2 className="w-4 h-4 mr-1" /> Delete
          </Button>
        </div>
      </div>

      {/* Brand Info Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {/* Logo */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Logo</CardTitle></CardHeader>
          <CardContent className="flex justify-center">
            {(store.brand?.logo_url || store.brand?.profile_image) ? (
              <img
                src={store.brand.logo_url || store.brand.profile_image}
                alt="Logo"
                className="h-20 w-20 object-contain rounded border bg-white p-2"
              />
            ) : (
              <div className="h-20 w-20 bg-gray-100 rounded flex items-center justify-center text-gray-400">
                No logo
              </div>
            )}
          </CardContent>
        </Card>

        {/* Colors */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Colors</CardTitle></CardHeader>
          <CardContent>
            <div className="flex gap-2 flex-wrap">
              {(store.brand?.colors || []).map((color, i) => (
                <div key={i} className="flex items-center gap-1">
                  <div
                    className="w-8 h-8 rounded border"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                  <span className="text-xs font-mono">{color}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Stats</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            <div className="flex justify-between">
              <span className="text-gray-500">Views</span>
              <span className="font-medium">{store.views}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Products</span>
              <span className="font-medium">{allProducts.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Collections</span>
              <span className="font-medium">{collections.length}</span>
            </div>
          </CardContent>
        </Card>

        {/* Category */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Category</CardTitle></CardHeader>
          <CardContent>
            <Badge className="mb-2">{store.source_type}</Badge>
            {store.category?.type && (
              <div className="text-sm text-gray-600">{store.category.type}</div>
            )}
            <div className="text-xs text-gray-400 mt-2">
              Created: {new Date(store.created_at).toLocaleString()}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Products Grid */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Products ({allProducts.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {allProducts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No products generated</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {allProducts.map((product, i) => (
                <div key={i} className="border rounded-lg overflow-hidden bg-white">
                  <div className="aspect-square bg-gray-50 relative">
                    {(product.image || product.image_url) ? (
                      <img
                        src={getImageUrl(product.image || product.image_url)}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        No image
                      </div>
                    )}
                    <Badge className="absolute top-1 left-1 text-xs" variant="secondary">
                      {product.collection}
                    </Badge>
                  </div>
                  <div className="p-2">
                    <div className="font-medium text-sm truncate">{product.name}</div>
                    <div className="text-xs text-gray-500">
                      {product.price}{product.currency || "€"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generations Table */}
      <Card>
        <CardHeader>
          <CardTitle>AI Generations ({store.generations?.length || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {!store.generations?.length ? (
            <div className="text-center py-4 text-gray-500">No generations recorded</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Prompt (preview)</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {store.generations?.map((gen) => (
                  <TableRow key={gen.id}>
                    <TableCell>
                      <Link to={`/admin/generations/${gen.id}`} className="text-blue-600 hover:underline">
                        #{gen.id}
                      </Link>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{gen.model}</TableCell>
                    <TableCell>
                      {gen.duration_ms ? `${(gen.duration_ms / 1000).toFixed(1)}s` : "-"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={gen.success ? "default" : "destructive"}>
                        {gen.success ? "Success" : "Failed"}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate text-xs text-gray-500">
                      {gen.prompt_text}
                    </TableCell>
                    <TableCell className="text-xs">
                      {new Date(gen.created_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
